import React, { useEffect, useState } from 'react';
import { apiFetch } from '../apiClient';

const INITIAL_FORM = {
  nom: '',
  url: '',
  type: 'wordpress',
  apiUrl: '',
  username: '',
  appPassword: '',
  supabaseUrl: '',
  supabaseKey: '',
  thematique: '',
  persona: '',
  ton: 'Professionnel',
  valeurUnique: '',
  sujetsInterdits: '',
  anglesPrioritaires: '',
  instructionsSujets: '',
};

function Sites() {
  const [sites, setSites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [error, setError] = useState('');

  const fetchSites = async () => {
    try {
      setError('');
      setIsLoading(true);
      const response = await apiFetch('/api/sites');
      if (!response.ok) {
        throw new Error('Erreur de chargement des sites');
      }
      const data = await response.json();
      setSites(Array.isArray(data) ? data : []);
    } catch (requestError) {
      setError('Impossible de charger les sites.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSites();
  }, []);

  const openModal = () => {
    setFormData(INITIAL_FORM);
    setEditingSite(null);
    setError('');
    setIsModalOpen(true);
  };

  const openEditModal = (site) => {
    setFormData({
      nom: site.nom || '',
      url: site.url || '',
      type: site.type || 'wordpress',
      apiUrl: site.apiUrl || '',
      username: site.username || '',
      appPassword: site.appPassword || '',
      supabaseUrl: site.supabaseUrl || '',
      supabaseKey: site.supabaseKey || '',
      thematique: site.thematique || '',
      persona: site.persona || '',
      ton: site.ton || 'Professionnel',
      valeurUnique: site.valeurUnique || '',
      sujetsInterdits: site.sujetsInterdits || '',
      anglesPrioritaires: site.anglesPrioritaires || '',
      instructionsSujets: site.instructionsSujets || '',
    });
    setEditingSite(site);
    setError('');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingSite(null);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setError('');
      const isEditing = Boolean(editingSite?.id);
      const payload =
        formData.type === 'supabase'
          ? { ...formData, apiUrl: '', username: '', appPassword: '' }
          : { ...formData, supabaseUrl: '', supabaseKey: '' };
      const response = await apiFetch(
        isEditing ? `/api/sites/${editingSite.id}` : '/api/sites',
        {
          method: isEditing ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error(isEditing ? 'Erreur de modification du site' : 'Erreur de création du site');
      }

      const savedSite = await response.json();
      if (isEditing) {
        setSites((current) => current.map((site) => (site.id === savedSite.id ? savedSite : site)));
      } else {
        setSites((current) => [savedSite, ...current]);
      }
      closeModal();
    } catch (requestError) {
      setError(editingSite ? 'Impossible de modifier le site.' : "Impossible d'ajouter le site.");
    }
  };

  const handleDelete = async (siteId) => {
    try {
      setError('');
      const response = await apiFetch(`/api/sites/${siteId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Erreur de suppression du site');
      }

      setSites((current) => current.filter((site) => site.id !== siteId));
    } catch (requestError) {
      setError('Impossible de supprimer ce site.');
    }
  };

  return (
    <section className="sites-page">
      <header className="sites-header">
        <div>
          <h1>Sites</h1>
          <p>Gérez vos destinations WordPress ou Supabase pour alimenter la machine SEO.</p>
        </div>
        <button type="button" className="primary-button sites-add-button" onClick={openModal}>
          Ajouter un site
        </button>
      </header>

      {error && <p className="sites-error">{error}</p>}

      {isLoading ? (
        <div className="sites-empty-card">
          <p>Chargement des sites...</p>
        </div>
      ) : sites.length === 0 ? (
        <div className="sites-empty-card">
          <h2>Aucun site enregistré</h2>
          <p>Ajoutez un site pour démarrer la production de contenus SEO.</p>
        </div>
      ) : (
        <div className="sites-grid">
          {sites.map((site) => (
            <article className="site-card" key={site.id}>
              <div className="site-card-header">
                <h3>{site.nom || 'Site sans nom'}</h3>
                <span className={`site-status ${site.actif ? 'active' : 'inactive'}`}>
                  {site.actif ? 'Actif' : 'Inactif'}
                </span>
              </div>
              <p className="site-url">{site.url || '-'}</p>
              <p className="site-thematique">
                <strong>Thématique :</strong> {site.thematique || '-'}
              </p>
              <p className="site-thematique">
                <strong>Type :</strong> {site.type || 'wordpress'}
              </p>
              <p className="site-thematique">
                <strong>Persona :</strong> {site.persona || '-'}
              </p>
              <p className="site-thematique">
                <strong>Ton :</strong> {site.ton || 'Professionnel'}
              </p>
              <div className="site-card-actions">
                <button
                  type="button"
                  className="site-edit-button"
                  onClick={() => openEditModal(site)}
                >
                  Modifier
                </button>
                <button
                  type="button"
                  className="site-delete-button"
                  onClick={() => handleDelete(site.id)}
                >
                  Supprimer
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {isModalOpen && (
        <div className="sites-modal-overlay" role="presentation" onClick={closeModal}>
          <div
            className="sites-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Ajouter un site WordPress"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="sites-modal-header">
              <h2>{editingSite ? 'Modifier le site' : 'Ajouter un site'}</h2>
              <button type="button" className="sites-close-button" onClick={closeModal}>
                ×
              </button>
            </header>

            <form className="sites-form" onSubmit={handleSubmit}>
              <label htmlFor="nom">Nom du site</label>
              <input id="nom" name="nom" value={formData.nom} onChange={handleChange} required />

              <label htmlFor="url">URL du site</label>
              <input id="url" name="url" value={formData.url} onChange={handleChange} required />

              {formData.type !== 'supabase' && (
                <>
                  <label htmlFor="apiUrl">URL API WordPress</label>
                  <input
                    id="apiUrl"
                    name="apiUrl"
                    placeholder="https://monsite.com/wp-json/wp/v2"
                    value={formData.apiUrl}
                    onChange={handleChange}
                    required
                  />

                  <label htmlFor="username">Username WordPress</label>
                  <input
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    required
                  />

                  <label htmlFor="appPassword">App Password WordPress</label>
                  <input
                    id="appPassword"
                    name="appPassword"
                    type="password"
                    value={formData.appPassword}
                    onChange={handleChange}
                    required
                  />
                </>
              )}

              {formData.type === 'supabase' && (
                <>
                  <label htmlFor="supabaseUrl">URL Supabase cible</label>
                  <input
                    id="supabaseUrl"
                    name="supabaseUrl"
                    placeholder="https://xxxx.supabase.co"
                    value={formData.supabaseUrl}
                    onChange={handleChange}
                    required
                  />

                  <label htmlFor="supabaseKey">Clé Supabase cible</label>
                  <input
                    id="supabaseKey"
                    name="supabaseKey"
                    type="password"
                    value={formData.supabaseKey}
                    onChange={handleChange}
                    required
                  />
                </>
              )}

              <label htmlFor="thematique">Thématique</label>
              <input
                id="thematique"
                name="thematique"
                value={formData.thematique}
                onChange={handleChange}
                required
              />

              <label htmlFor="persona">
                Persona : Décrivez votre lecteur idéal (ex: organisateur de festival, responsable associatif...)
              </label>
              <textarea
                id="persona"
                name="persona"
                value={formData.persona}
                onChange={handleChange}
                rows={3}
                placeholder="Décrivez votre lecteur idéal"
              />

              <label htmlFor="ton">Ton éditorial</label>
              <select id="ton" name="ton" value={formData.ton} onChange={handleChange}>
                <option value="Professionnel">Professionnel</option>
                <option value="Accessible">Accessible</option>
                <option value="Technique">Technique</option>
                <option value="Inspirant">Inspirant</option>
                <option value="Pratique">Pratique</option>
              </select>

              <label htmlFor="valeurUnique">Valeur unique : Qu'est-ce qui différencie votre site/produit ?</label>
              <textarea
                id="valeurUnique"
                name="valeurUnique"
                value={formData.valeurUnique}
                onChange={handleChange}
                rows={3}
                placeholder="Votre proposition de valeur unique"
              />

              <label htmlFor="sujetsInterdits">
                Sujets interdits : Sujets à éviter absolument (concurrents, sujets sensibles...)
              </label>
              <textarea
                id="sujetsInterdits"
                name="sujetsInterdits"
                value={formData.sujetsInterdits}
                onChange={handleChange}
                rows={3}
                placeholder="Liste des sujets à éviter"
              />

              <label htmlFor="anglesPrioritaires">Angles prioritaires : Thèmes et angles éditoriaux à privilégier</label>
              <textarea
                id="anglesPrioritaires"
                name="anglesPrioritaires"
                value={formData.anglesPrioritaires}
                onChange={handleChange}
                rows={3}
                placeholder="Thèmes et angles éditoriaux prioritaires"
              />

              <label htmlFor="instructionsSujets">Instructions de génération de sujets</label>
              <textarea
                id="instructionsSujets"
                name="instructionsSujets"
                value={formData.instructionsSujets}
                onChange={handleChange}
                rows={4}
                placeholder="Ex: Proposer des sujets d'actualité tech récente, éviter les tutos basiques, privilégier les analyses et opinions..."
              />

              <label htmlFor="type">Type de destination</label>
              <select id="type" name="type" value={formData.type} onChange={handleChange}>
                <option value="wordpress">wordpress</option>
                <option value="supabase">supabase</option>
              </select>

              <div className="sites-form-actions">
                <button type="button" className="secondary-button" onClick={closeModal}>
                  Annuler
                </button>
                <button type="submit" className="primary-button">
                  {editingSite ? 'Mettre à jour' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}

export default Sites;
